<template>
    <div class="hle-approval-review-page">
        <button type="button" class="boq-approval-setup-back hle-approval-review-back" @click="BackToItem">
            <i class="fa-solid fa-chevron-left"></i>
            <span>{{ $t('Back') }}</span>
        </button>

        <section class="hle-approval-review-panel">
            <header class="hle-approval-review-hero">
                <div class="hle-approval-review-hero__main">
                    <span class="hle-approval-review-eyebrow">{{ $t('Approval Module.Approval of') }}</span>
                    <h1>{{ boqItem.name || '-' }}</h1>
                    <router-link
                        v-if="boqItem.hleProjectId && boqItem._id"
                        :to="{name : 'HleBoQBuilder', params : {id : boqItem.hleProjectId, boqId : boqItem._id} }"
                        class="hle-approval-review-reference"
                    >
                        <i class="fa-solid fa-up-right-from-square"></i>
                        <span>{{ $t('Bidding Module.Detailed Estimates') }}</span>
                    </router-link>
                </div>

                <div class="hle-approval-review-hero__side">
                    <span :class="'hle-bidding-status-pill ' + ApprovalStatusClass">{{ ApprovalStatusLabel }}</span>
                    <dl class="hle-approval-review-dates">
                        <div>
                            <dt>{{ $t('Approval Module.Sent Date') }}</dt>
                            <dd>{{ ReformatDate(approvalItem.createdAt) || '-' }}</dd>
                        </div>
                        <div>
                            <dt>{{ $t('Approval Module.Expected End Date') }}</dt>
                            <dd>{{ ReformatDate(approvalItem.dateEnd) || '-' }}</dd>
                        </div>
                    </dl>
                </div>
            </header>

            <div class="hle-approval-review-workflow">
                <BoqProcessWorkFlow
                    v-if="approvalItem._id"
                    :processItemData="approvalItem" 
                    :showApprovalDisapprove="true"
                />
            </div>

            <div class="hle-approval-review-summary">
                <div class="hle-approval-review-report-head">
                    <div class="hle-approval-review-report-field">
                        <span>{{ $t('Bill of Quantity') }}</span>
                        <strong>{{ boqItem.name || '-' }}</strong>
                    </div>
                    <div class="hle-approval-review-report-field">
                        <span>{{ $t('Reference') }}</span>
                        <strong>{{ ApprovalReference }}</strong>
                    </div>
                    <div class="hle-approval-review-report-field">
                        <span>{{ $t('Currency') }}</span>
                        <strong>{{ CurrencyLabel }}</strong>
                    </div>
                </div>
                <FigureCurrencyNote :currency="boqItem.currencyId" />
                <div class="hle-approval-review-table">
                    <BoqSectionTableReadOnly
                        v-if="boqItem"
                        :rawSections="rawSections"
                        :boqItem="boqItem"
                        :approvalId="approvalItem._id"
                        :breakdownLink="breakdownLink"
                    />
                </div>
            </div>
        </section>
        
        <div class="hle-approval-review-comments">
            <ProcessCommentList 
                :PageChangeComment="PageChangeComment"
                :processItem="approvalItem"
                :commentList="commentList"
            />
        </div>

        <div v-if="CanApprove && approvalItem">
            <div class="hle-approval-review-action-spacer"></div>
            <div class="hle-approval-review-action-dock">
                <button class="btn hle-approval-review-action hle-approval-review-action--approve" data-bs-toggle="modal" data-bs-target="#BoqReviewApprove">
                    <i class="fa-solid fa-thumbs-up"></i>
                    <span>{{$t('Approve')}}</span>
                </button>
                <button class="btn hle-approval-review-action hle-approval-review-action--revision" data-bs-toggle="modal" data-bs-target="#BoqReviewReject">
                    <i class="fa-solid fa-thumbs-down"></i>
                    <span>{{$t('Approval Module.Ask For Revision')}}</span>
                </button>
            </div>
        </div>
        <Modal idModal="BoqReviewApprove" :headerTitle="$t('Approve')"  >
            <div>
                <div class="form-group">
                    <label>{{ $t('Approval Module.Notes (optional)') }}: </label>
                    <textarea v-model="approveNotes" class="form-control"></textarea>
                </div>
                
            </div>
            <template v-slot:actions>
                <button type="button" ref="closeBoqReviewApprove" class="btn btn-outline-danger" data-bs-dismiss="modal">X {{$t('Cancel')}}</button>
                <button type="button" class="btn btn-primary" @click="Approve" >
                    <i class="fas fa-thumbs-up"></i> {{$t('Approve')}}
                </button>
            </template>
        </Modal>
        <Modal idModal="BoqReviewReject" :headerTitle="$t('Approval Module.Ask For Revision')"  >
            <div>
                <div class="form-group">
                    <label>{{$t('Approval Module.Notes')}} <span class="bidding-required-inline" v-if="!rejectNotes">({{ $t('Required').toLowerCase() }})</span></label>
                    <textarea v-model="rejectNotes" class="form-control"></textarea>
                </div>
                
            </div>
            <template v-slot:actions>
                <button type="button" ref="closeBoqReviewReject" class="btn btn-outline-danger" data-bs-dismiss="modal">X Cancel</button>
                <button type="button" class="btn btn-primary" @click="Disapprove" >
                    <i class="fas fa-thumbs-down"></i> {{$t('Approval Module.Ask For Revision')}}
                </button>
            </template>
        </Modal>
    </div>
</template>

<script>
import {GetHighLevelBoq, GetHleNewBoqCreatorSectionsRevisions} from '@/actions/boqActions.js'  
import {GetWorkflowItem, GetCommentsList, UserApproveWorkFlow, UserDisapproveWorkFlow} from '@/actions/projectActions.js'
import {ReformatDate} from '@/helpers/utilities.js'
import ProcessCommentList  from '@/components/projectInformation/ProcessCommentList.vue'
import BoqProcessWorkFlow  from '@/components/projectInformation/BoqProcessWorkFlow.vue'
import Modal  from '@/components/layout/Modal.vue'
import {ProcessActionTypes} from '@/helpers/utilities.js'
import {mapGetters} from 'vuex'
import BoqSectionTableReadOnly  from '@/components/boq/BoqSectionTableReadOnly.vue'
import FigureCurrencyNote from '@/components/layout/FigureCurrencyNote.vue'
export default {
    name : 'HleBoqItemsApproval',
    computed : {
        ...mapGetters(['userInfo']),
        CanApprove(){
            const self = this
            const approvers = self.approvalItem.internalApprovers
            if(approvers.findIndex(u=>u.relatedUserId?._id==self.userInfo._id && u.action==ProcessActionTypes.PENDING&& u.actionType==2) >-1){
                return true
            }

            return false
        },
        ApprovalStatusLabel(){
            const self = this
            if(self.approvalItem.status==3){
                return self.$i18n.t('Approval Module.Completed')
            }
            if(self.approvalItem.status==2){
                return self.$i18n.t('Approval Module.External') + ' ' + self.$i18n.t('Approval Module.Approval')
            }
            return self.$i18n.t('Approval Module.Internal') + ' ' + self.$i18n.t('Approval Module.Approval')
        },
        ApprovalStatusClass(){
            if(this.approvalItem.status==3){
                return 'hle-bidding-status-pill--approved'
            }
            if(this.approvalItem.status==2){
                return 'hle-bidding-status-pill--approval'
            }
            return 'hle-bidding-status-pill--draft'
        },
        ApprovalReference(){
            const reference = this.boqItem.reference
            if(!reference){
                return '-'
            }
            return [reference.quotationNumber, reference.quotationName].filter(Boolean).join(' - ') || '-'
        },
        CurrencyLabel(){
            const currency = this.boqItem.currencyId
            return currency?.name || currency?.sign || '-'
        }
    },
    components : {
        ProcessCommentList, BoqProcessWorkFlow, Modal
        , BoqSectionTableReadOnly,FigureCurrencyNote
    },
    methods : {
        ReformatDate,
        BackToItem(){
            const self = this
            if(self.boqItem?.hleProjectId && self.boqItem?._id){
                self.$router.push({name : 'HleBoQBuilder', params : {id : self.boqItem.hleProjectId, boqId : self.boqItem._id}})
                return
            }
            self.$router.back()
        },
        Approve(e){
            const self = this
            const btn = e.target


            if(self.isApproveRunning){
                return
            }

            btn.disabled = true
            self.isApproveRunning = true
            UserApproveWorkFlow(self.approvalItem._id,self.approveNotes)
            .then((res)=>{
                self.isApproveRunning = false
                btn.disabled = false
                if(res!="OK"){
                    self.$swal(self.$i18n.t('Approval Module.Approve'),res.value,'warning')
                }else{
                    self.$swal({
                        icon: 'success',
                        title: self.$i18n.t('Approved successfully'),
                        showConfirmButton: false,
                        timer : 1000
                    })
                    location.reload()
                }
            })
        },
        Disapprove(e){
            const self = this
            const btn = e.target

            if(self.isDisapproveRunning){
                return
            }

            if(!self.rejectNotes){
                return
            }

            self.isDisapproveRunning = true
            btn.disabled = true
            UserDisapproveWorkFlow(self.approvalItem._id, self.rejectNotes)
            .then((res)=>{
                btn.disabled = false
                self.isDisapproveRunning = false
                if(res!="OK"){
                    self.$swal(self.$i18n.t('Approval Module.Ask For Revision'),res.value,'warning')
                }else{
                    self.$swal({
                        icon: 'success',
                        title: self.$i18n.t('Item marked Asked for Revision successfully'),
                        showConfirmButton: false,
                        timer : 1000
                    })
                    location.reload()
                }
            })
        },
        async PageChangeComment(page){
            const self = this
            self.commentFilterParam.page = page
            self.commentList = await GetCommentsList(self.commentFilterParam)
        },
    },
    mounted : async function(){
        const self = this

        self.approvalItem = await GetWorkflowItem(self.approvalId)
        self.rawSections = await GetHleNewBoqCreatorSectionsRevisions(self.approvalItem.relatedId,self.approvalItem.relatedRevisionId)
        
        self.isDataReadyToLoad = true
        self.boqItem = await GetHighLevelBoq(self.approvalItem.relatedId)
        self.commentList = await GetCommentsList(self.commentFilterParam)
        self.breakdownLink.params.id = self.approvalItem._id
        self.breakdownLink.query.revisionId = self.approvalItem.relatedRevisionId
    },
    data(){
        return {
            rawSections : {
                sectionList : [],
                overAllTotal :{}
            },
            approvalItem : {
                internalApprovers : [],
                createdBy : {}
            },
            approvalId : this.$route.params.id,
            boqItem : {
                _id : 1,
                currencyId : {}
            },
            selectedColumns : [],
            commentList : {
                docs : [],
                hasNextPage : false
            },
            commentFilterParam : {
                page : 1,
                id : this.$route.params.id,
            },
            isDataReadyToLoad : false,
            rejectNotes : '',
            approveNotes : '',
            isApproveRunning : false,
            isDisapproveRunning : false,
            breakdownLink : {name : 'HleBoqItemsApprovalBreakdown',params : {}, query : {}}
        }
    },

}
</script>
